"use client";

import { Core } from "@faire/design-tokens";
import { getWindow } from "@faire/web/common/globals/getWindow";
import { useStrictLocalization } from "@faire/web/common/localization";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Add } from "@faire/web/slate/icons/foundation/Add";
import { Chevron } from "@faire/web/slate/icons/foundation/Chevron";
import { Close } from "@faire/web/slate/icons/foundation/Close";
import { FormInputLabel, Input } from "@faire/web/slate/Input";
import { Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import ClickOutside from "@faire/web/ui/ClickOutside";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import * as React from "react";
import { useTheme } from "styled-components";

import { useDropdownState } from "./__internal__/useDropdownState";
import {
  getLabel,
  getModularIndex,
  getValue,
  isCustomOption,
} from "./__internal__/utils";
import { DROPDOWN_LIST_HEIGHT, ROW_SIZE } from "./constants";
import {
  CloseWrapper,
  CreateNewOptionDropdownItem,
  CustomEditSpan,
  DropdownItem,
  DropdownWrapper,
  EditOptionButton,
  MobileChevronWrapper,
  MobileInnerWrapper,
  MobileSelectWrapper,
  OptionRow,
  StyledSelect,
  Wrapper,
} from "./styles";
import { IProps } from "./types";

/**
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/slate/component/dropdown)
 */
export const Dropdown = <T extends string = string>(props: IProps<T>) => {
  const { strictLocalize } = useStrictLocalization();
  const {
    options,
    filter,
    onSelect,
    value,
    containingElement,
    disableInput,
    className,
    placeholder,
    label,
    error,
    helper,
    createNewOption,
    disabled,
    autoComplete = "off",
    id,
    inputRef,
    borderless,
    customOption,
    "aria-label": ariaLabel,
    "data-test-id": dataTestId,
    "data-hj-allow": dataHjAllow,
    useTabletAndAboveDropdownStyle,
    onDropdownOpen,
    onDropdownClose,
  } = props;

  const {
    dropdownId,
    targetRef,
    refs,
    open,
    setOpen,
    searchValue,
    setSearchValue,
    highlightIndex,
    setHighlightIndex,
    isCustomizingOption,
    setIsCustomizingOption,
    filteredOptions,
    ariaProps,
    showErrorState,
    showErrorMessage,
    errorMessage,
    showMobileDropdown,
  } = useDropdownState({
    id,
    inputRef,
    filter,
    options,
    customOption,
    createNewOption,
    ariaLabel,
    label,
    error,
    useTabletAndAboveDropdownStyle,
  });

  const theme = useTheme();

  const handleInputOnChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    if (!isCustomizingOption) {
      handleDropdownOpen();

      const search = event.target.value;
      setSearchValue(search);

      if (search === "") {
        setHighlightIndex(0);
        scrollIntoView(0);
        return;
      }

      const optionIndex = filteredOptions.findIndex((e) =>
        getLabel(e).toLowerCase().startsWith(search.toLowerCase())
      );
      if (optionIndex !== -1) {
        setHighlightIndex(optionIndex);
        scrollIntoView(optionIndex);
      }
    } else {
      setSearchValue(event.target.value);
    }
  };

  const enterCustomValue = () => {
    if (customOption && searchValue) {
      customOption.onValueChange(searchValue);
      setIsCustomizingOption(false);
      setSearchValue(undefined);
    }
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter") {
      if (isCustomizingOption) {
        enterCustomValue();
      } else if (props.createNewOption && filteredOptions.length === 0) {
        setSearchValue(undefined);
        if (props.createNewOption.closeDropdown) {
          setOpen(false);
        }
        props.createNewOption?.onClick?.(searchValue);
      }
    }
  };

  const handleClickOutside = () => {
    if (isCustomizingOption) {
      enterCustomValue();
    } else {
      handleDropdownClose();
    }
  };

  const handleSelectDropDown = (optionValue: T) => {
    let option = undefined;
    if (customOption && getValue(customOption) === optionValue) {
      option = customOption;
    } else {
      option = options.find((e) => getValue(e) === optionValue);
    }
    onSelect(option!);
    setOpen(false);
    setSearchValue(undefined);
  };

  const handleDropdownOpen = () => {
    onDropdownOpen?.();
    setOpen(true);
    if (!value) {
      return;
    }

    const selectedIndex =
      customOption && customOption.value === value
        ? options.length
        : options.findIndex((o) => o.value === value);

    if (selectedIndex !== -1) {
      setHighlightIndex(selectedIndex);
    }
  };

  const handleDropdownClose = () => {
    onDropdownClose?.();
    setSearchValue(undefined);
    setOpen(false);
  };

  const handleSelectDropDownItem = (
    event: React.MouseEvent<HTMLElement>,
    index: number
  ) => {
    event.preventDefault();
    select(index);
  };

  const handleEditCustomOption = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setIsCustomizingOption(true);

    handleDropdownClose();
    if (targetRef.current) {
      targetRef.current.focus();
    }

    setSearchValue(customOption?.hasEdited ? customOption.value : "");
  };

  const select = (selectIndex: number) => {
    const selectedOption = filteredOptions[selectIndex];
    const value = selectedOption ? getValue(selectedOption) : undefined;

    if (value !== undefined) {
      handleSelectDropDown(value);
    }
  };

  const scrollIntoView = (index: number) => {
    const ref = refs.current[index];
    if (!ref) {
      return;
    }
    ref.scrollIntoView?.({
      block: "nearest",
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (["Tab", "Escape"].includes(event.key)) {
      handleDropdownClose();
      return;
    }

    const isRelevantKey = ["ArrowDown", "ArrowUp", "Enter"].includes(event.key);
    if (!isRelevantKey) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (open) {
      let nextIndex = highlightIndex;
      switch (event.key) {
        case "ArrowDown":
        case "ArrowUp":
          nextIndex = getNextIndex(event.key);
          break;
        case "Enter":
          select(highlightIndex);
          break;
        default:
          break;
      }

      scrollIntoView(nextIndex);
      setHighlightIndex(nextIndex);
    } else {
      handleDropdownOpen();
    }
  };

  /**
   * get the next non-disabled index
   */
  const getNextIndex = (eventKey: "ArrowDown" | "ArrowUp"): number => {
    const optionsCount = filteredOptions.length;
    let nextIndex = highlightIndex;

    for (let i = 0; i < optionsCount; ++i) {
      nextIndex = nextIndex + (eventKey === "ArrowDown" ? 1 : -1);
      nextIndex = getModularIndex(nextIndex, optionsCount);

      const nextOption = filteredOptions[nextIndex];
      if (nextOption && !nextOption.disabled) {
        return nextIndex;
      }
    }
    // we've iterated over all possibilities, let's keep the same option
    return highlightIndex;
  };

  const onHighlight = (index: number) => () => {
    setHighlightIndex(index);
  };

  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (targetRef.current) {
      targetRef.current.setSelectionRange(0, targetRef.current.value.length);
    }
    if (open) {
      handleDropdownClose();
    } else {
      handleDropdownOpen();
    }
    e.preventDefault();
    e.stopPropagation();
  };

  const currentSelectedLabel = filteredOptions.find(
    (o) => o.value === value
  )?.label;

  // Returns true if the dropdown should open up, false otherwise.
  const calculateIsUp = () => {
    const sizeOfDropdown =
      ROW_SIZE * Math.min(DROPDOWN_LIST_HEIGHT / ROW_SIZE, options.length);
    const potentialBottom = targetRef.current?.getBoundingClientRect().top;
    const potentialTop = targetRef.current?.getBoundingClientRect().bottom;

    if (potentialBottom === undefined || potentialTop === undefined) {
      return false;
    }

    // If we're going to go above the screen, it's better to go down
    if (potentialBottom - sizeOfDropdown < 0) {
      return false;
    }

    return (
      potentialTop + sizeOfDropdown >
      (containingElement?.getBoundingClientRect()?.bottom ??
        getWindow()?.innerHeight ??
        0)
    );
  };

  const displayValue = disableInput
    ? currentSelectedLabel ?? ""
    : searchValue ?? currentSelectedLabel ?? "";

  if (showMobileDropdown) {
    const isValueInOptions = filteredOptions.some(
      (option) => option.value === value
    );
    const validValue = isValueInOptions ? value : "";
    return (
      <MobileSelectWrapper className={className}>
        {isNotUndefined(label) ? (
          <FormInputLabel
            $typography={{
              ...(theme?.input.label ?? {}),
              color: showErrorState
                ? "error"
                : theme?.input.label?.color ?? Core.text.default,
            }}
          >
            {label}
          </FormInputLabel>
        ) : null}
        <MobileInnerWrapper
          $borderless={borderless}
          $showErrorState={showErrorState}
          $disabled={disabled}
        >
          <MobileChevronWrapper>
            <Chevron
              direction="down"
              color={disabled ? Core.icon.disabled : Core.icon.default}
              width="8"
              height="8"
              strokeWidth={2}
            />
          </MobileChevronWrapper>
          <StyledSelect
            id={dropdownId}
            data-test-id={dataTestId}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore used for tests, even though it's not a valid prop on select in some React versions.
            placeholder={placeholder}
            disabled={disabled}
            onClick={(e) => {
              onDropdownOpen?.();
              e.preventDefault();
              e.stopPropagation();
            }}
            onChange={(e) => handleSelectDropDown(e.target.value as T)}
            value={validValue || ""}
          >
            <option value="" disabled>
              {placeholder}
            </option>
            {filteredOptions.map((option) => (
              <option
                data-test-id={`dropdownItem_${option.value}`}
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </StyledSelect>
        </MobileInnerWrapper>

        {helper && !showErrorMessage ? (
          <>
            <Spacer size={4} />
            <Typography
              {...{
                color: "pewter", // TODO: map new color https://fairewholesale.atlassian.net/browse/FD-127193
                ...(theme?.input.helper ?? {}),
              }}
            >
              {helper}
            </Typography>
          </>
        ) : null}
        {showErrorMessage ? (
          <>
            <Spacer size={4} />
            <Typography variant="labelSansRegular" color={Core.text.critical}>
              {errorMessage}
            </Typography>
          </>
        ) : null}
      </MobileSelectWrapper>
    );
  }

  const renderCreateNewOptionButton = () => {
    if (!props.createNewOption) {
      return null;
    }

    if (
      props.createNewOption.hideUnlessNoOptions &&
      (!searchValue ||
        filteredOptions.some(
          (option) =>
            option.label.toLocaleLowerCase() ===
            searchValue.toLocaleLowerCase().trim()
        ))
    ) {
      return null;
    }

    const index = filteredOptions.length + 1;
    const createNewOptionLabel =
      typeof props.createNewOption.label === "function"
        ? props.createNewOption.label(searchValue)
        : props.createNewOption.label;

    return (
      <CreateNewOptionDropdownItem
        key="dropdownItem_CustomizedOption"
        role="option"
        tabIndex={-1}
        onClick={() => {
          if (props.createNewOption?.closeDropdown) {
            setOpen(false);
          }

          if (props.createNewOption?.hideUnlessNoOptions) {
            setSearchValue(undefined);
          }

          props.createNewOption?.onClick?.(searchValue);
        }}
        data-test-id="dropdownItem_CustomizedOption"
        type="button"
        ref={(el: HTMLButtonElement | null) => {
          if (!el) {
            return;
          }
          refs.current[index] = el;
        }}
        onMouseOver={onHighlight(index)}
        $highlight={index === highlightIndex}
      >
        <Row align="center">
          <Add
            color={Core.icon.default}
            height={getSpacing("2x")}
            width={getSpacing("2x")}
            aria-label={strictLocalize({
              defaultMessage: "Add new option value",
              description: {
                text: "Message for screen readers when selecting a customized option value",
              },
            })}
          />
          <Spacer size="1x" />
          {createNewOptionLabel}
        </Row>
      </CreateNewOptionDropdownItem>
    );
  };

  const renderSuffixNode = () => {
    if (value && props.onClear) {
      return null;
    }
    return (
      <Chevron
        direction="down"
        width="8"
        height="8"
        color={disabled ? Core.icon.disabled : Core.icon.default}
        strokeWidth={2}
        margin={`0 0 0 ${getSpacing("1x")}`}
      />
    );
  };

  const renderClearButton = () => {
    if (value && props.onClear) {
      return (
        <CloseWrapper
          onClick={props.onClear}
          $hasHelperText={!!helper}
          data-test-id="option-clear-button"
          type="button"
        >
          <Close
            color={Core.icon.subdued}
            titleAccess={strictLocalize({
              defaultMessage: "Clear selection",
              description: {
                text: "Description of icon to clear the selection for screen readers",
              },
            })}
          />
        </CloseWrapper>
      );
    }
    return null;
  };

  return (
    <Wrapper
      data-test-id={`dropdown-${label || "default"}`}
      className={className}
      $disabled={disabled}
      $disableInput={disableInput}
      {...ariaProps}
    >
      <ClickOutside
        onClickOutside={handleClickOutside}
        onKeyDown={handleKeyDown}
      >
        <Input
          id={dropdownId}
          data-test-id={dataTestId}
          disabled={disabled}
          label={label}
          helper={helper}
          error={error}
          value={displayValue}
          placeholder={placeholder}
          inputMode={disableInput ? "none" : undefined}
          onChange={(e) => {
            if (disableInput !== true) {
              handleInputOnChange(e);
            }
          }}
          onClick={handleInputClick}
          style={{ width: "100%" }}
          autoComplete={autoComplete}
          suffixNode={renderSuffixNode()}
          ref={targetRef}
          borderless={borderless}
          spellCheck={!disableInput}
          aria-label={ariaLabel}
          onKeyDown={handleInputKeyDown}
          data-hj-allow={dataHjAllow}
        />
        {open ? (
          <DropdownWrapper
            data-test-id="dropdown_wrapper"
            $showErrorMessage={showErrorMessage}
            $hasHelper={!!helper}
            $isOpen={open}
            $isUp={calculateIsUp()}
            role="listbox"
            aria-multiselectable={false}
          >
            {filteredOptions.map((option, index) => {
              const label = getLabel(option);
              const value = getValue(option);
              const isSelected = index === highlightIndex;
              const isCustom = isCustomOption(option);

              return (
                <OptionRow
                  key={`${label} ${index}`}
                  $highlight={isSelected}
                  align="center"
                  onMouseOver={onHighlight(index)}
                >
                  <DropdownItem
                    role="option"
                    ref={(el: HTMLButtonElement | null) => {
                      if (!el) {
                        return;
                      }
                      refs.current[index] = el;
                    }}
                    tabIndex={-1}
                    $isCustom={isCustom && option.hasEdited}
                    onClick={
                      isCustom
                        ? option.onClick ??
                          (option.hasEdited
                            ? (event) => handleSelectDropDownItem(event, index)
                            : handleEditCustomOption)
                        : undefined
                    }
                    onMouseDown={
                      isCustom
                        ? undefined
                        : (event) => handleSelectDropDownItem(event, index)
                    }
                    onMouseOver={onHighlight(index)}
                    data-test-id={`dropdownItem_${value}`}
                    type="button"
                    aria-selected={isSelected}
                    disabled={option.disabled}
                  >
                    {label}
                  </DropdownItem>
                  {isCustom && option.hasEdited ? (
                    <EditOptionButton
                      onClick={handleEditCustomOption}
                      onMouseOver={onHighlight(index)}
                      data-test-id={`custom_dropdownItem_${value}`}
                      type="button"
                      aria-selected={isSelected}
                    >
                      <CustomEditSpan>
                        <StrictLocalMsg
                          defaultMessage="Edit"
                          description={{
                            text: "A button named Edit the let user know they can edit an option.",
                            img: "https://cdn.faire.com/fastly/e918de51aa45bab72dc3117b6e2bfad3a224a44af020ff47a19466fcaf91b401.png",
                          }}
                        />
                      </CustomEditSpan>
                    </EditOptionButton>
                  ) : null}
                </OptionRow>
              );
            })}

            {renderCreateNewOptionButton()}
          </DropdownWrapper>
        ) : null}
      </ClickOutside>
      {renderClearButton()}
    </Wrapper>
  );
};

Dropdown.displayName = "Dropdown";
